import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  CheckCircle,
  CloudUpload,
  Image,
  Description,
  AccountBox,
  CreditCard,
  CameraAlt,
} from "@mui/icons-material";
import ApiEndpoints, { BASE_URL } from "../network/ApiEndPoints";
import { postJsonData } from "../network/ApiController";
import { apiErrorToast, okSuccessToast } from "../utils/ToastUtil";
import AuthContext from "../store/AuthContext";
import useCommonContext from "../store/CommonContext";
import axios from "axios";
import MyButton from "../component/MyButton";
import { Delete } from "@mui/icons-material";

const UserDocument = ({}) => {
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(false);
  const [images, setImages] = useState({
    aadhar1: null,
    aadhaar2: null,
    pan: null,
    shop: null,
    bank: null,
    gst: null,
    photo: null,
    other1: null,
    other2: null,
    other3: null,
    other4: null,
  });
  const [previews, setPreviews] = useState({});
  const [errors, setErrors] = useState({});
  const [kycImages, setKycImages] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const authCtx = useContext(AuthContext);
  const user = authCtx.user;
  const token = authCtx.token;
  const { refreshUser } = useCommonContext();
  const [openModal, setOpenModal] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  useEffect(() => {
    if (user.is_document === 1) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [user.is_document]);

  const handleClose = () => {
    if (user.is_document === 0) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (user?.kyc_images) {
      setKycImages(JSON.parse(user.kyc_images));
    }
  }, [user]);

  const handleFileChange = (e, field) => {
    if (user?.kyc_status === 1) return;
    const file = e.target.files[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/png", "image/jpg"];
      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [field]: "Invalid file type. Only jpg, jpeg, png are allowed.",
        }));
        return;
      }

      if (file.size > 512 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [field]: "File size exceeds 512 KB.",
        }));
        return;
      }

      setImages((prev) => ({ ...prev, [field]: file }));
      setPreviews((prev) => ({
        ...prev,
        [field]: URL.createObjectURL(file),
      }));
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const getImage = async (fileName) => {
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await axios.post(
        `${BASE_URL}/${ApiEndpoints.GET_FILES}`,
        { fileName },
        { responseType: "blob", headers: headers }
      );

      const imageUrl = URL.createObjectURL(new Blob([response.data]));
      return imageUrl;
    } catch (error) {
      console.error("Error fetching image:", error);
      return null;
    }
  };

  const fetchImage = async (filename) => {
    const fileName = kycImages[filename];
    const url = await getImage(fileName);
    if (url) {
      setOpenModal(true);
      setImageUrl(url);
    }
  };

  const validate = () => {
    const newErrors = {};
    let isValid = true;

    const requiredFields = ["aadhar1", "aadhaar2", "pan", "photo", "shop"];

    requiredFields.forEach((field) => {
      if (!images[field] && !kycImages[field]) {
        newErrors[field] = `${
          field === "aadhaar2"
            ? "Aadhar Back"
            : field === "aadhar1"
            ? "Aadhar Front"
            : field.charAt(0).toUpperCase() + field.slice(1)
        } is required.`;
        isValid = false;
      }
    });

    setErrors(newErrors);
    setIsFormValid(isValid);
    return isValid;
  };

  const handleRemoveImage = (field) => {
    setKycImages((prev) => {
      const newKycImages = { ...prev };
      delete newKycImages[field];
      return newKycImages;
    });

    setPreviews((prev) => {
      const newPreviews = { ...prev };
      delete newPreviews[field];
      return newPreviews;
    });

    const requiredFields = ["aadhar1", "aadhaar2", "pan", "photo", "shop"];
    if (requiredFields.includes(field)) {
      setErrors((prev) => ({
        ...prev,
        [field]: "This document is required",
      }));
    }
  };
  const handleSubmit = async () => {
    if (!validate()) return;

    const formData = new FormData();

    Object.keys(images).forEach((key) => {
      if (images[key]) {
        formData.append(key, images[key]); // New uploads as binary
      }
    });

    const existingFields = Object.keys(kycImages).filter((key) => !images[key]);

    try {
      // Fetch all existing documents that haven't been updated
      const existingDocs = await Promise.all(
        existingFields.map(async (key) => {
          try {
            const url = await getImage(kycImages[key]);
            const response = await fetch(url);
            const blob = await response.blob();
            return { key, blob };
          } catch (error) {
            console.error(`Error fetching existing ${key}:`, error);
            return null;
          }
        })
      );

      // Add the fetched documents to formData
      existingDocs.forEach((doc) => {
        if (doc) {
          // Convert filename to original field name (remove _existing if present)
          const fieldName = doc.key.replace("_existing", "");
          formData.append(fieldName, doc.blob);
        }
      });

      // 3. Now send the request
      postJsonData(
        ApiEndpoints.UPLOAD_USER_KYC,
        formData,
        setProgress,
        (res) => {
          okSuccessToast("Documents uploaded successfully");
          setUploadSuccess(true);
          refreshUser();

          // Reset form state
          setImages({
            aadhar1: null,
            aadhaar2: null,
            pan: null,
            shop: null,
            bank: null,
            gst: null,
            photo: null,
            other1: null,
            other2: null,
            other3: null,
            other4: null,
          });
          setPreviews({});
          setErrors({});
        },
        (err) => {
          apiErrorToast(err);
        }
      );
    } catch (error) {
      console.error("Error processing existing documents:", error);
      apiErrorToast("Error processing documents. Please try again.");
    }
  };

  const handleReset = () => {
    setImages({
      aadhar1: null,
      aadhaar2: null,
      pan: null,
      shop: null,
      bank: null,
      gst: null,
      photo: null,
      other1: null,
      other2: null,
      other3: null,
      other4: null,
    });
    setPreviews({});
    setErrors({});
  };

  const renderFileIcon = (field) => {
    switch (field) {
      case "aadhar1":
      case "aadhaar2":
        return <AccountBox />;
      case "pan":
        return <CreditCard />;
      case "photo":
        return <CameraAlt />;
      case "shop":
      case "bank":
      case "gst":
        return <Description />;
      default:
        return <Image />;
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose}>
        <DialogContent>
          <Box p={4}>
            <Typography>
              Your Admin KYC is pending. Please upload your documents to
              proceed.
            </Typography>
            <Grid container spacing={3}>
              {[
                { label: "Aadhar Front", field: "aadhar1", required: true },
                { label: "Aadhar Back", field: "aadhaar2", required: true },
                { label: "PAN Card", field: "pan", required: true },
                { label: "Shop Image", field: "shop", required: true },
                { label: "Bank Statement", field: "bank", required: false },
                { label: "Photo", field: "photo", required: true },
                {
                  label: "Sample Signature",
                  field: "signature",
                  required: false,
                },
                { label: "GST Certificate", field: "gst", required: false },
                { label: "Other Document 1", field: "other1", required: false },
                { label: "Other Document 2", field: "other2", required: false },
                { label: "Other Document 3", field: "other3", required: false },
                { label: "Other Document 4", field: "other4", required: false },
              ].map(({ label, field, required }) => (
                <Grid item xs={12} sm={6} md={4} key={field}>
                  {kycImages[field] ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      sx={{ boxShadow: 2 }}
                      gap={1}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <CheckCircle color="success" />
                        <IconButton
                          onClick={() => handleRemoveImage(field)}
                          size="small"
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" align="center">
                        {label} is already uploaded.
                      </Typography>
                      <MyButton
                        text={`View ${label}`}
                        onClick={() => fetchImage(field)}
                        sx={{
                          marginTop: 2,
                          background: "#1976d2",
                          color: "#fff",
                          "&:hover": {
                            background: "#115293",
                          },
                        }}
                      />
                    </Box>
                  ) : user?.kyc_status === 0 || !user?.kyc_status ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      sx={{ boxShadow: 4 }}
                    >
                      <IconButton
                        component="label"
                        htmlFor={field}
                        sx={{ color: "primary.main", fontSize: 40 }}
                      >
                        {renderFileIcon(field)}
                      </IconButton>
                      <TextField
                        type="file"
                        inputProps={{ accept: "image/*" }}
                        id={field}
                        hidden
                        onChange={(e) => handleFileChange(e, field)}
                      />
                      <Typography variant="caption" mt={1}>
                        {label} {required && "*"}
                      </Typography>
                      {errors[field] && (
                        <Typography color="error" variant="body2">
                          {errors[field]}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      align="center"
                    >
                      Viewing only. Upload disabled.
                    </Typography>
                  )}
                  {previews[field] && (
                    <Box mt={2} position="relative">
                      <img
                        src={previews[field]}
                        alt={label}
                        style={{ width: "100%", borderRadius: 4 }}
                      />
                      <IconButton
                        onClick={() => {
                          setImages((prev) => ({ ...prev, [field]: null }));
                          setPreviews((prev) => {
                            const newPreviews = { ...prev };
                            delete newPreviews[field];
                            return newPreviews;
                          });
                        }}
                        size="small"
                        color="error"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "rgba(255,255,255,0.7)",
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Grid>
              ))}
            </Grid>

            <Box
              display="flex"
              justifyContent="space-between"
              flexDirection={{ xs: "column", sm: "row" }}
              mt={3}
              gap={2}
            >
              <Button
                onClick={handleSubmit}
                disabled={
                  (!Object.values(images).some((file) => file) &&
                    Object.keys(kycImages).length === 0) ||
                  progress
                }
                variant="contained"
                color="primary"
              >
                {progress ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Upload"
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                color="secondary"
                disabled={progress}
              >
                Reset
              </Button>
            </Box>
          </Box>
        </DialogContent>
        {uploadSuccess && (
          <Box mt={3} p={2} bgcolor="#e8f5e9" borderRadius={2}>
            <Typography variant="body1" color="success.main" align="center">
              All documents uploaded successfully! Please contact admin for
              verification.
            </Typography>
          </Box>
        )}
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>Document View</DialogTitle>
        <DialogContent>
          <img
            src={imageUrl}
            alt="Document"
            style={{
              width: "100%",
              height: "auto",
              borderRadius: 8,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default UserDocument;
